import { useState, useEffect } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, FormGroup, CircularProgress,
  FormControl, FormControlLabel, Radio, Autocomplete, TextField, Stepper, Step, StepLabel, Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { getProductsAndPreparationsLight, getProductById, getProductsForPackaging, getSomeProducts } from '../../services/ProductService';
import { getPreparationById, getPreparationsForPackaging } from '../../services/PreparationService';
import { saveElaboration, savePackaging } from '../../services/ProductionService';
import { getUnitById, referenceTypes } from '../../utils/Enums';
import { productionTypeByProcess } from './productionTypes';
import dayjs from 'dayjs';
import SnackbarUtils from '../../utils/SnackbarUtils';

const isInvalidQuantity = (v) => v === '' || v === null || v === undefined || isNaN(Number(v)) || Number(v) < 0;

const AddProductionModal = ({ locationId, open, onSave, onClose }) => {
    const { t } = useTranslation();

    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [processType, setProcessType] = useState('elaboration');
    const [options, setOptions] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [batchSize, setBatchSize] = useState('');
    const [actualBatchSize, setActualBatchSize] = useState('');
    const [stock, setStock] = useState(0);
    const [ingredients, setIngredients] = useState([]);
    const [packagingOptions, setPackagingOptions] = useState([]);
    const [productionDate, setProductionDate] = useState(new Date().toISOString().split('T')[0]);
    const [expirationDate, setExpirationDate] = useState(dayjs().add(6, 'month').format('YYYY-MM-DD'));
    const [lotNumber, setLotNumber] = useState('');
    const [ingredientLots, setIngredientLots] = useState({});
    const [packagingIngredients, setPackagingIngredients] = useState([]);

    // Función para calcular fecha de vencimiento basada en el tipo de producto
    const calculateExpirationDate = (item) => {
        if (!item) return dayjs().add(6, 'month').format('YYYY-MM-DD');
        
        // Si es un producto con categoría específica
        if (item.category) {
            const categoryName = item.category.name?.toLowerCase() || '';
            
            // Productos frescos: 7 días
            if (categoryName.includes('fresco') || categoryName.includes('verdura') || categoryName.includes('fruta')) {
                return dayjs().add(7, 'day').format('YYYY-MM-DD');
            }
            
            // Productos lácteos: 30 días
            if (categoryName.includes('lácteo') || categoryName.includes('leche') || categoryName.includes('queso')) {
                return dayjs().add(30, 'day').format('YYYY-MM-DD');
            }
            
            // Productos cárnicos: 14 días
            if (categoryName.includes('carne') || categoryName.includes('pollo') || categoryName.includes('pescado')) {
                return dayjs().add(14, 'day').format('YYYY-MM-DD');
            }
            
            // Productos horneados: 3 días
            if (categoryName.includes('pan') || categoryName.includes('horneado') || categoryName.includes('pastel')) {
                return dayjs().add(3, 'day').format('YYYY-MM-DD');
            }
        }
        
        // Si es una preparación, usar tiempo de vida útil específico
        if (item.shelfLife) {
            return dayjs().add(item.shelfLife, 'day').format('YYYY-MM-DD');
        }
        
        // Por defecto: 6 meses para productos procesados
        return dayjs().add(6, 'month').format('YYYY-MM-DD');
    };

    useEffect(() => {
        if (activeStep === 1) {
            if (processType === 'elaboration') {
                fetchProductsAndPreparations(locationId).then(setOptions);
            } else if (processType === 'packaging') {
                fetchPreparationsForPackaging(locationId).then(setOptions);
            }
        }
    }, [activeStep, processType]);

    useEffect(() => {
        if (activeStep === 2 && processType === 'packaging' && selectedItem) {
            setStock(selectedItem.stock || 0);
        }
    }, [activeStep, selectedItem, processType]);

    // Actualizar fecha de vencimiento cuando se selecciona un producto
    useEffect(() => {
        if (selectedItem) {
            const calculatedExpirationDate = calculateExpirationDate(selectedItem);
            setExpirationDate(calculatedExpirationDate);
        }
    }, [selectedItem]);

    useEffect(() => {
        if (activeStep === 3 && selectedItem) {
            if (processType === 'elaboration' && batchSize) {
                setIngredients(extractIngredients(selectedItem, batchSize));
            } else if (processType === 'packaging') {
                fetchProductsForPackaging(selectedItem.id).then(setPackagingOptions);
            }
        }
    }, [activeStep, selectedItem, batchSize, processType]);

    useEffect(() => {

        if (activeStep === 4 && processType === 'packaging') {
            fetchIngredientsForPackaging();
        }

    }, [activeStep, processType, packagingOptions]);

    // Preselecciona por FEFO: el lote vigente que vence antes (sin vencimiento al final). No pisa lo que ya eligió el usuario.
    const pickFefoLot = (lots = []) => {
        const now = dayjs();
        const valid = lots.filter(lot => !lot.expirationDate || !dayjs(lot.expirationDate).isBefore(now));
        const sorted = [...valid].sort((a, b) => {
            if (!a.expirationDate && !b.expirationDate) return 0;
            if (!a.expirationDate) return 1;
            if (!b.expirationDate) return -1;
            return dayjs(a.expirationDate).valueOf() - dayjs(b.expirationDate).valueOf();
        });
        return sorted[0];
    };

    useEffect(() => {
        const seed = {};
        [...ingredients, ...packagingIngredients].forEach(ingredient => {
            if (ingredient.usesLot === false || ingredientLots[ingredient.id] !== undefined) return;
            const lot = pickFefoLot(ingredient.lots);
            if (lot) seed[ingredient.id] = [lot];
        });
        if (Object.keys(seed).length > 0) {
            setIngredientLots(prev => ({ ...seed, ...prev }));
        }
    }, [ingredients, packagingIngredients]);

    const fetchIngredientsForPackaging = async () => {
        try {
            setLoading(true);
            
            const productIds = packagingOptions
                .filter(product => product.packagedQuantity > 0)
                .map(product => product.id);

            if (productIds.length === 0) return;
    
            const response = await getSomeProducts(locationId, productIds);

            // Objeto para agrupar insumos por referenciaId + unidad
            const ingredientMap = new Map();
    
            response.forEach(product => {
                const packagedQuantity = parseFloat(packagingOptions.find(p => p.id === product.id).packagedQuantity);
                const allIngredients = [...product.content, ...product.packaging, ...product.toppings];
                allIngredients.forEach(ingredient => {
                    const key = `${ingredient.referenceId1}-${ingredient.unit}`;
                    const qty = ingredient.quantity * packagedQuantity;
                    if (ingredientMap.has(key)) {
                        const entry = ingredientMap.get(key);
                        entry.expectedQuantity += qty;
                        entry.actualQuantity += qty;
                    } else {
                        ingredientMap.set(key, {
                            id: ingredient.id,
                            referenceName: ingredient.referenceName,
                            referenceId: ingredient.referenceId || ingredient.referenceId1,
                            referenceType: ingredient.referenceType,
                            expectedQuantity: qty,
                            unit: ingredient.unit,
                            actualQuantity: qty,
                            lots: ingredient.lots || [],
                            usesLot: ingredient.usesLot !== false
                        });
                    }
                });
            });
    
            // Convertir el mapa a un array y actualizar el estado
            setPackagingIngredients(Array.from(ingredientMap.values()));
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };    

    const fetchProductsAndPreparations = async (locationId) => {
        try {
            setLoading(true);
            const data = await getProductsAndPreparationsLight(locationId);
            const products = (data.products || []).map(p => ({
                id: p.id,
                name: p.name,
                referenceType: referenceTypes.PRODUCTS,
                batch: 1,
                unit: 5
            }));
            const preparations = (data.preparations || []).map(p => ({
                id: p.id,
                name: p.name,
                batch: p.batch,
                unit: p.unit,
                referenceType: referenceTypes.PREPARADO
            }));
            setLoading(false);
            return [...products, ...preparations].sort((a, b) => a.name.localeCompare(b.name));
        } catch (error) {
            console.error("Error fetching products and preparations:", error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const loadFullItemOnSelect = async (option) => {
        if (!option?.id) return option;
        const hasFullDetail = Array.isArray(option.items) || Array.isArray(option.content);
        if (hasFullDetail) return option;
        try {
            setLoading(true);
            if (option.referenceType === referenceTypes.PRODUCTS) {
                const full = await getProductById(option.id, locationId);
                // El detalle de un producto no incluye unidad porque los productos
                // siempre se producen por unidad. Conservamos el dato del selector.
                return { ...option, ...full, referenceType: referenceTypes.PRODUCTS };
            }
            if (option.referenceType === referenceTypes.PREPARADO) {
                const full = await getPreparationById(option.id, locationId);
                return { ...option, ...full, referenceType: referenceTypes.PREPARADO };
            }
            return option;
        } catch (e) {
            console.error('Error loading full item:', e);
            return option;
        } finally {
            setLoading(false);
        }
    };    

    const fetchPreparationsForPackaging = async (locationId) => {
        try {
            setLoading(true);
            const data = await getPreparationsForPackaging(locationId);
            setLoading(false);
            return (data || []).map(preparation => ({
                ...preparation,
                referenceType: referenceTypes.PREPARADO
            }));
        } catch (error) {
            console.error("Error fetching preparations for packaging:", error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const fetchProductsForPackaging = async (preparationId) => {
        try {
            setLoading(true);
            const data = await getProductsForPackaging(preparationId);
            setLoading(false);
            return data.map(product => ({
                id: product.id,
                name: product.name,
                unit: product.unit,
                packagedQuantity: ''
            }));
        } catch (error) {
            console.error("Error fetching products for packaging:", error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const extractIngredients = (item, newBatchSize) => {
        const baseBatch = item.batch || 1;
        const allItems = [
            ...(item.items || []),
            ...(item.packaging || []),
            ...(item.content || []),
            ...(item.toppings || []),
            ...(item.definedContent || []),
            ...(item.undefinedContent || [])
        ];
        return allItems.map(ingredient => {
            const expectedQty = (ingredient.quantity * newBatchSize) / baseBatch;
            return {
                id: ingredient.id,
                name: ingredient.referenceName,
                referenceId: ingredient.referenceId || ingredient.referenceId1,
                referenceType: ingredient.referenceType,
                expectedQuantity: expectedQty,
                unit: ingredient.unit,
                actualQuantity: expectedQty,
                lots: ingredient.lots || [],
                usesLot: ingredient.usesLot !== false
            };
        });
    };

    const steps = processType === 'elaboration'
    ? [t('select_process_type'), t('select_item_to_process'), t('set_production_details'), t('set_real_quantities')]
    : [t('select_process_type'), t('select_item_to_process'), t('set_production_details'), t('set_real_quantities'), t('confirm_ingredients')];

    const handleNext = () => {
        switch (activeStep) {
            case 0:
                if (!processType) {
                    SnackbarUtils.error("Selecciona un tipo de proceso.");
                    return;
                }
                break;
            case 1:
                if (!selectedItem) {
                    SnackbarUtils.error("Selecciona un producto o preparación.");
                    return;
                }
                break;
            case 2:
                if (processType === 'elaboration') {
                    if (!batchSize || batchSize <= 0 || !actualBatchSize || actualBatchSize <= 0) {
                        SnackbarUtils.error("Tamaño de lote inválido.");
                        return;
                    }
                    if (!productionDate || !expirationDate || !lotNumber) {
                        SnackbarUtils.error("Completa todas las fechas y número de lote.");
                        return;
                    }
                } else if (processType === 'packaging') {
                    if (!productionDate || !expirationDate) {
                        SnackbarUtils.error("Completa cantidad, fecha de producción y vencimiento.");
                        return;
                    }
                }
                break;
            case 3:
                if (processType === 'elaboration') {
                    const invalidQty = ingredients.find(i => isInvalidQuantity(i.actualQuantity));
                    if (invalidQty) {
                        SnackbarUtils.error(`Ingresá una cantidad real válida (0 o más) para "${invalidQty.name}".`);
                        return;
                    }
                    const missingLot = ingredients.find(i => i.usesLot !== false && Number(i.actualQuantity) > 0 && !(ingredientLots[i.id] && ingredientLots[i.id].length > 0));
                    if (missingLot) {
                        SnackbarUtils.error(`Seleccioná al menos un lote para "${missingLot.name}" o poné su cantidad real en 0.`);
                        return;
                    }
                    if (!lotNumber.trim()) {
                        SnackbarUtils.error("Número de lote requerido.");
                        return;
                    }
                } else if (processType === 'packaging') {
                    const invalidPackaged = packagingOptions.find(p => isInvalidQuantity(p.packagedQuantity));
                    if (invalidPackaged) {
                        SnackbarUtils.error(`Ingresá una cantidad envasada válida (0 o más) para "${invalidPackaged.name}".`);
                        return;
                    }
                }
                break;
            case 4:
                if (processType === 'packaging') {
                    const invalidQty = packagingIngredients.find(i => isInvalidQuantity(i.actualQuantity));
                    if (invalidQty) {
                        SnackbarUtils.error(`Ingresá una cantidad real válida (0 o más) para "${invalidQty.name}".`);
                        return;
                    }
                    const missingLot = packagingIngredients.find(i => i.usesLot !== false && Number(i.actualQuantity) > 0 && !(ingredientLots[i.id] && ingredientLots[i.id].length > 0));
                    if (missingLot) {
                        SnackbarUtils.error(`Seleccioná al menos un lote para "${missingLot.name}" o poné su cantidad real en 0.`);
                        return;
                    }
                }
                break;
            default:
                break;
        }

        if (activeStep === steps.length - 1) {
            handleSave();
            return;
        }

        setActiveStep((prev) => prev + 1);
    };    

    const handleSave = async () => {
        setLoadingButton(true);

        let formattedProductionDate = dayjs(productionDate).format('YYYY-MM-DDTHH:mm:ss');
        let formattedExpirationDate = dayjs(expirationDate).format('YYYY-MM-DDTHH:mm:ss');

        let requestData = {
            reference_id: selectedItem.id,
            reference_type: selectedItem.referenceType,
            production_date: formattedProductionDate,
            expiration_date: formattedExpirationDate,
            lot_number: lotNumber
        };
        
        if (processType === 'elaboration') {
            requestData = {
                ...requestData,
                production_type: productionTypeByProcess.elaboration,
                unit: selectedItem.unit,
                batch_size: {
                    theoretical: parseFloat(batchSize),
                    actual: parseFloat(actualBatchSize)
                },
                ingredients: ingredients.map(ingredient => ({
                    id: parseInt(ingredient.referenceId),
                    reference_type: parseInt(ingredient.referenceType),
                    unit: parseInt(ingredient.unit),
                    actual_quantity: parseFloat(ingredient.actualQuantity),
                    lots: ingredientLots[ingredient.id]?.map(lot => (lot.id.toString())) || []
                }))
            };
        } else if (processType === 'packaging') {
            requestData = {
                ...requestData,
                production_type: productionTypeByProcess.packaging,
                packaging_options: packagingOptions.map(product => ({
                    product_id: parseInt(product.id),
                    packaged_quantity: parseFloat(product.packagedQuantity)
                })),
                ingredients: packagingIngredients.map(ingredient => ({
                    id: parseInt(ingredient.referenceId),
                    reference_type: parseInt(ingredient.referenceType),
                    unit: parseInt(ingredient.unit),
                    actual_quantity: parseFloat(ingredient.actualQuantity),
                    lots: ingredientLots[ingredient.id]?.map(lot => lot.id.toString()) || []
                }))
            };
        }

        try {
            let response;
            if (processType === 'elaboration') {
                response = await saveElaboration(locationId, requestData);
            } else if (processType === 'packaging') {
                response = await savePackaging(locationId, requestData);
            }
            setLoadingButton(false);
            onSave(response);
        } catch (error) {
            console.error("Error al guardar la producción:", error);
        } finally {
            setLoadingButton(false);
        }
    };
    
    const handleBack = () => {
        setActiveStep((prev) => {
            const newStep = prev - 1;
            switch (newStep) {
                case 0:
                    setSelectedItem(null);
                    break;
                case 1:
                    setBatchSize('');
                    setActualBatchSize('');
                    setProductionDate(new Date().toISOString().split('T')[0]);
                    setExpirationDate(calculateExpirationDate(selectedItem)); // Fecha calculada automáticamente según el tipo de producto
                    setLotNumber('');
                    setStock(0);
                    break;
                case 2:
                    setIngredients([]);
                    setPackagingOptions([]);
                    setIngredientLots({});
                    break;
                default:
                    break;
            }
            return newStep;
        });
    };

    const handleIngredientChange = (index, value) => {
        const updatedIngredients = [...ingredients];
        updatedIngredients[index].actualQuantity = value;
        setIngredients(updatedIngredients);
    };

    const handlePackagingChange = (index, value) => {
        const updatedPackagingOptions = [...packagingOptions];
        updatedPackagingOptions[index].packagedQuantity = value;
        setPackagingOptions(updatedPackagingOptions);
    };
    
    const handleLotChange = (ingredientId, selectedLots) => {
        setIngredientLots(prev => ({
            ...prev,
            [ingredientId]: selectedLots
        }));
    };

    const handlePackagingIngredientChange = (index, value) => {
        const updatedIngredients = [...packagingIngredients];
        updatedIngredients[index].actualQuantity = value;
        setPackagingIngredients(updatedIngredients);
    };
    
    return (
        <Dialog 
            open={open} 
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                onClose(); // Solo cerrar si se llama explícitamente
                }
            }}
            maxWidth="md" 
            fullWidth
        >
            <DialogTitle>{t('add_production')}</DialogTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                <Stepper activeStep={activeStep} sx={{ width: '800px', my: 2 }}>
                    {steps.map(label => (
                        <Step key={label}>
                            <StepLabel>{t(label)}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>
            <DialogContent>
            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '300px' // Ajusta la altura para centrar bien el loader
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {activeStep === 0 && (
                        <Grid container justifyContent="center">
                            <FormControl component="fieldset">
                                <FormGroup row>
                                    <FormControlLabel 
                                        value="elaboration" 
                                        control={<Radio />} 
                                        label={t('elaboration_process')} 
                                        checked={processType === "elaboration"}
                                        onChange={(e) => setProcessType(e.target.value)}
                                    />
                                    <FormControlLabel 
                                        value="packaging" 
                                        control={<Radio />} 
                                        label={t('packaging_process')} 
                                        checked={processType === "packaging"}
                                        onChange={(e) => setProcessType(e.target.value)}
                                    />
                                </FormGroup>
                            </FormControl>
                        </Grid>                
                    )}

                    {activeStep === 1 && (
                        <Autocomplete
                            options={options}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) =>
                                option.id === value.id && option.referenceType === value.referenceType
                            }
                            value={selectedItem}
                            onChange={async (event, newValue) => {
                                if (!newValue) {
                                    setSelectedItem(null);
                                    return;
                                }
                                const full = await loadFullItemOnSelect(newValue);
                                setSelectedItem(full);
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label={t('select_item_to_process')} fullWidth />
                            )}
                        />
                    )}

                    {activeStep === 2 && (
                        <>
                            {processType === 'elaboration' && (
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                label={t('batch_size_theorist')}
                                                type="number"
                                                value={batchSize}
                                                onChange={(e) => setBatchSize(e.target.value)}
                                                fullWidth
                                                margin="dense"
                                                placeholder={`${t('enter_value')} ${getUnitById(selectedItem.unit)?.label || 'N/A'}`}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label={t('batch_size_real')}
                                                type="number"
                                                value={actualBatchSize}
                                                onChange={(e) => setActualBatchSize(e.target.value)}
                                                fullWidth
                                                margin="dense"
                                                placeholder={`${t('enter_value')} ${getUnitById(selectedItem.unit)?.label || 'N/A'}`}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label={t('production_date')}
                                                type="date"
                                                value={productionDate}
                                                onChange={(e) => setProductionDate(e.target.value)}
                                                fullWidth
                                                margin="dense"
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2} style={{marginTop: "2px"}}>
                                        <Grid item xs={6}>
                                            <TextField
                                                label={t('lot_number')}
                                                type="text"
                                                value={lotNumber}
                                                onChange={(e) => setLotNumber(e.target.value)}
                                                fullWidth
                                                margin="dense"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label={t('expiration_date')}
                                                type="date"
                                                value={expirationDate}
                                                onChange={(e) => setExpirationDate(e.target.value)}
                                                fullWidth
                                                margin="dense"
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            )}

                            {processType === 'packaging' && selectedItem && (
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Box>
                                                <strong>{t('current_stock')}:</strong> {stock.toFixed(2)}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label={t('production_date')}
                                                type="date"
                                                value={productionDate}
                                                onChange={(e) => setProductionDate(e.target.value)}
                                                fullWidth
                                                margin="dense"
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2} style={{marginTop: "2px"}}>
                                        <Grid item xs={6}>
                                            <TextField
                                                label={t('lot_number')}
                                                type="text"
                                                value={lotNumber}
                                                onChange={(e) => setLotNumber(e.target.value)}
                                                fullWidth
                                                margin="dense"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label={t('expiration_date')}
                                                type="date"
                                                value={expirationDate}
                                                onChange={(e) => setExpirationDate(e.target.value)}
                                                fullWidth
                                                margin="dense"
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </>
                    )}

                    {activeStep === 3 && (
                        <>
                        {processType === 'elaboration' && (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width="20%">{t('ingredient')}</TableCell>
                                        <TableCell width="10%">{t('expected_quantity')}</TableCell>
                                        <TableCell width="15%">{t('unit')}</TableCell>
                                        <TableCell width="15%">{t('real_quantity')}</TableCell>
                                        <TableCell width="40%">{t('used_lots')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ingredients.map((ingredient, index) => (
                                        <TableRow key={ingredient.id}>
                                            <TableCell>{ingredient.name}</TableCell>
                                            <TableCell>{ingredient.expectedQuantity.toFixed(2)}</TableCell>
                                            <TableCell>{getUnitById(ingredient.unit)?.label || 'N/A'}</TableCell>
                                            <TableCell>
                                                <TextField
                                                    type="number"
                                                    value={ingredient.actualQuantity}
                                                    onChange={(e) => handleIngredientChange(index, e.target.value)}
                                                    fullWidth
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {ingredient.usesLot !== false ? (
                                                    <Autocomplete
                                                        multiple
                                                        options={ingredient.lots}
                                                        getOptionLabel={(option) => `${option.lot} (${option.expirationDate ? dayjs(option.expirationDate).format('DD/MM/YYYY') : 'N/A'})`}
                                                        value={ingredientLots[ingredient.id] || []}
                                                        onChange={(event, newValue) => handleLotChange(ingredient.id, newValue)}
                                                        renderInput={(params) => <TextField {...params} label={t('select_lots')} fullWidth />}
                                                    />
                                                ) : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        {processType === 'packaging' && (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('product')}</TableCell>
                                        <TableCell>{t('packaging_quantity')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {packagingOptions.map((product, index) => (
                                        <TableRow key={product.id}>
                                            <TableCell>{product.name}</TableCell>
                                            <TableCell>
                                                <TextField
                                                    type="number"
                                                    placeholder="En unidades"
                                                    value={product.packagedQuantity}
                                                    onChange={(e) => handlePackagingChange(index, e.target.value)}
                                                    fullWidth
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        </>
                    )}

                    {activeStep === 4 && processType === 'packaging' && (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="20%">{t('ingredient')}</TableCell>
                                    <TableCell width="10%">{t('expected_quantity')}</TableCell>
                                    <TableCell width="15%">{t('unit')}</TableCell>
                                    <TableCell width="15%">{t('real_quantity')}</TableCell>
                                    <TableCell width="40%">{t('used_lots')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {packagingIngredients.map((ingredient, index) => (
                                    <TableRow key={ingredient.id}>
                                        <TableCell>{ingredient.referenceName}</TableCell>
                                        <TableCell>{ingredient.expectedQuantity.toFixed(2)}</TableCell>
                                        <TableCell>{getUnitById(ingredient.unit)?.label || 'N/A'}</TableCell>
                                        <TableCell>
                                            <TextField
                                                type="number"
                                                value={ingredient.actualQuantity}
                                                onChange={(e) => handlePackagingIngredientChange(index, e.target.value)}
                                                fullWidth
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {ingredient.usesLot !== false ? (
                                                <Autocomplete
                                                    multiple
                                                    options={ingredient.lots}
                                                    getOptionLabel={(option) => `${option.lot} (${option.expirationDate ? dayjs(option.expirationDate).format('DD/MM/YYYY') : 'N/A'})`}
                                                    value={ingredientLots[ingredient.id] || []}
                                                    onChange={(event, newValue) => handleLotChange(ingredient.id, newValue)}
                                                    renderInput={(params) => <TextField {...params} label={t('select_lots')} fullWidth />}
                                                />
                                            ) : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </>
            )}
            </DialogContent>
            <DialogActions>
                <Button onClick={activeStep === 0 ? onClose : handleBack}>
                    {activeStep === 0 ? t('close') : t('back')}
                </Button>
                <LoadingButton loading={loadingButton} onClick={handleNext} variant="contained">
                    {activeStep === steps.length - 1 ? t('save') : t('next')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default AddProductionModal;
